import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import {
  Badge,
  Button,
  ColumnManager,
  EmptyState,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  ValuePills,
  namePills,
} from '@/components/ui';
import { FilterableTH, type FilterableTHProps } from '@/components/ui/ColumnFilter';
import { TextLink } from '@/components/ui/TextLink';
import { Tooltip } from '@/components/ui/Tooltip';
import { ViewToggle } from '@/components/ui/ViewToggle';
import {
  RevenueMatrixTable,
  type MatrixVisibilityProps,
} from '@/components/dashboards/RevenueMatrixTable';
import { MATRIX_VISIBILITY_SCOPES } from '@/lib/matrix-visibility';
import { useMatrixVisibility } from '@/lib/use-matrix-visibility';
import { cx } from '@/lib/cx';
import { sumDecimalStrings } from '@/lib/decimal';
import { formatDecimalString, pluralise } from '@/lib/format';
import {
  buildProductCompanyMatrix,
  type CompanyMatrixLine,
  type MatrixRow,
} from '@/lib/revenue-matrix';
import { compactMoneyParts } from '@/lib/targets';
import type { RevenueProductCycle } from '@/lib/types';
import { CompactFigure } from '@/components/dashboards/TargetTiles';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import {
  NO_PRODUCT_MEANING,
  UNCODED_BADGE,
  XERO_UNCODED_MEANING,
  companyIdOfMatrixKey,
  matrixCyclesOf,
  nativeTotalsText,
  overCountSentence,
  readProductExpandView,
  shareOf,
  shareText,
  writeProductExpandView,
  type ProductExpandView,
  type ProductGroup,
} from './revenue-product';

/**
 * THE PRODUCT TABLE — what sold, what share of the scope that is, and one click
 * down, in which cycles and which regions.
 *
 * ── ⚠️ WHAT THE COLUMNS MAY CLAIM ─────────────────────────────────────────
 *
 * Every figure is **one currency, paid invoices only, incl. tax** (owner
 * 2026-10-16; *SUPERSEDED: ex-tax* — the heading reads `taxLabel`), inside the
 * chosen cycle scope. The currency code renders against every amount rather
 * than only in the heading — a row read on its own, or screenshotted, has to
 * carry its own unit, because two currencies are never added here.
 *
 * 🔴 **`—` IS "NOTHING IN THIS CURRENCY" AND NEVER `0.00`.** A product nobody
 * bought and a product that sold for exactly zero are different facts.
 *
 * 🔴 **THE UNCATALOGUED BUCKET IS LISTED WITH EVERYTHING ELSE AND BADGED ON
 * EVERY ROW.** It is real revenue and it is not a product; dropping it lies
 * about where the money came from and listing it unmarked lies about what was
 * sold. See `UNCODED_PRODUCT_CODE`.
 *
 * ── 🔴 FOUR COLUMNS, AND THE FILTERS LIVE ON THEM ──────────────────────────
 *
 * ⚠️ *SUPERSEDED, on the owner's instruction 2026-09-11: seven columns — a rank,
 * a per-row sparkline headed "Trend · each row to its own peak", and a
 * "Movement" column carrying Growing/Declining/Stopped badges against two cycles
 * chosen in a panel above.* **The cycle comparison is gone as a feature**, and
 * the trend column with it; what is left is the question the tab is named for.
 *
 * ⚠️ **THE RANK COLUMN WENT WITH THEM, AND NOT ONLY FOR SPACE.** A column headed
 * `#` beside a product reads as its rank among all products. That was true while
 * the only order was revenue-descending; with the headers now sortable, a `#`
 * beside a list ordered A–Z is a rank in nothing — a position index wearing a
 * ranking's clothes. The order is whatever the reader chose, and the revenue
 * column states it.
 *
 * ⚠️ **`Share` CARRIES NO FUNNEL, DELIBERATELY.** It is `total / total-on-screen`,
 * so ordering by it is ordering by `Paid revenue` exactly — two headers offering
 * one order is two controls a reader has to discover are the same. Its values
 * are continuous, so there is nothing to tick either. **Adding one would be a
 * reversal, not a gap being filled** — and the column manager below does not
 * change that: it decides whether a column is SHOWN, never whether it filters.
 *
 * ── ⚠️ FOUR IS THE DECLARED SET, NOT THE RENDERED ONE ──────────────────────
 *
 * Since the column manager landed, a reader chooses which of the four they see,
 * in what order, and which stick to an edge — stored against their account by
 * `useColumnLayout`. **Nothing in this file may count the columns**: the header,
 * the body row and the expanded row's `colSpan` are all driven from
 * `layout.visibleKeys`, and a literal four would be wrong for every reader who
 * has ever touched the control, silently. See `COLUMN_KEYS` and `LOCKED_KEYS`.
 */

/* -------------------------------------------------------------------------
 * Cells
 * --------------------------------------------------------------------- */

/**
 * How many rows a fresh view shows before the reader asks for the rest.
 *
 * ⚠️ **SET ABOVE THE REAL CEILING ON PURPOSE, so that today it never fires.**
 * Measured 2026-09-11: the catalogue holds 28 products, and a group is one
 * product in one currency — so the list tops out at 29 rows including the
 * uncatalogued bucket. At 25 the footer offered *"Show all 27"* to reveal **two
 * rows**, which is a control that costs a reader more attention than the rows it
 * was hiding.
 *
 * ⚠️ **THE MECHANISM IS KEPT RATHER THAN DELETED.** The catalogue is
 * admin-maintained and grows by an INSERT, so the day it doubles this is the
 * only thing between a summary panel and a 60-row scroll. Raising the threshold
 * costs nothing while the list is short; removing the truncation would have to
 * be rebuilt.
 */
const HEAD_ROWS = 40;

/** A remembered list of row keys (filter memory), or `undefined`. */
function parseStringArray(raw: unknown): readonly string[] | undefined {
  return Array.isArray(raw) && raw.every((key) => typeof key === 'string')
    ? (raw as string[])
    : undefined;
}

/* -------------------------------------------------------------------------
 * The column model
 * --------------------------------------------------------------------- */

/**
 * Where this table's arrangement is stored, against the account.
 *
 * ⚠️ **THE SCOPE STRING IS THE STORAGE KEY**, so renaming it silently drops
 * every reader's saved arrangement. It is not a label and nothing renders it.
 */
const LAYOUT_SCOPE = 'revenue.product';

/** The one column this file names itself; the other three come from the panel. */
const SHARE_COLUMN = 'share';

/**
 * Every column, in its DECLARED order.
 *
 * ⚠️ **DECLARED, NOT RENDERED.** `useColumnLayout` treats this as the
 * authority: a key a reader has moved is honoured, a key this list GAINS lands
 * in its declared position rather than at the end, and a key it DROPS vanishes
 * from a stored order rather than resurrecting a column. Module-level so its
 * identity is stable across renders.
 *
 * ⚠️ **THE FIRST THREE MUST SPELL THE PANEL'S `ColumnSpec` KEYS EXACTLY.**
 * `headerFor` returns `{ label: key }` for a key no column declares rather than
 * throwing, so a typo here renders a lower-case heading with **no funnel and no
 * sort** — a control that quietly stopped existing, which is the safe direction
 * and still wrong.
 */
const COLUMN_KEYS = ['product', 'cycles', 'revenue', SHARE_COLUMN] as const;

/**
 * 🔴 **`product` IS LOCKED BECAUSE IT HOLDS THE ONLY CONTROL ON THE ROW.** The
 * disclosure chevron is rendered *inside* this cell — a product has no detail
 * page in this app, so the expanded row is the whole of the per-cycle and
 * per-region answer, and hiding this column would remove the capability rather
 * than a value. It also identifies the row, which is the lesser of the two
 * reasons.
 *
 * A locked key is still LISTED in the manager, disabled, so its absence cannot
 * read as the control having lost it.
 */
const LOCKED_KEYS = ['product'] as const;

/**
 * Why `product` cannot be hidden, in the reader's terms.
 *
 * 🔴 **IT NAMES THE CONTROL, NOT THE IDENTITY, BECAUSE THAT IS THE STRONGER OF
 * THE TWO REASONS ABOVE.** The manager's own fallback — *"This column cannot be
 * hidden"* — is true and says nothing, and a sentence about identifying the row
 * would be the LESSER reason: hiding this column does not merely leave the
 * figures anonymous, it removes the only route into the per-cycle and
 * per-region breakdown, because the disclosure button lives in this cell and a
 * product has no detail page in this app.
 *
 * ⚠️ **A why-this-is-disabled sentence, which the no-helper-text rule keeps by
 * name** — a bare `disabled` makes a locked column and a broken switch
 * identical. Answered per key rather than returned unconditionally, so a second
 * lock added here cannot inherit this one's reason.
 */
function lockReasonOf(key: string): string | undefined {
  return key === 'product'
    ? 'The cycle and region-of-focus breakdown opens from this column. Hiding it would remove the only way in.'
    : undefined;
}

/**
 * Each column's width request AND its floor.
 *
 * 🔴 **THE `min-w-` FLOORS ARE WHAT USED TO BE THE TABLE'S `min-w-[760px]`,
 * REDISTRIBUTED.** They are proportional to the four width requests and sum to
 * exactly 760, so a table with every column showing has the floor it has always
 * had — and one with a column hidden drops by that column's share instead of
 * holding a width for something that is not there.
 *
 * ⛔ **IT IS STILL NOT A COMPUTED FLOOR ON THE TABLE, AND ONE OF THE TWO
 * REASONS IT COULD NOT BE HAS EXPIRED.** ⚠️ *SUPERSEDED: "`Table` takes
 * `className` and `children` and **no `style`**, so there is no inline route to
 * the `<table>` at all."* **`Table` takes a `style` now**, and the sibling
 * tables use it — so that half of the argument is dead and must not be quoted
 * on.
 *
 * **What survives is the reason that was always the better one: a floor summed
 * on the TABLE holds width for a column that is not there.** These are
 * per-CELL floors, so hiding a column drops the table's minimum by that
 * column's own share automatically, with nothing to keep in step. A single
 * `style={{ minWidth }}` here would have to be re-summed from `visibleKeys` on
 * every render to say the same thing, and would be wrong the moment somebody
 * forgot. (The other half stands regardless: Tailwind v4 scans SOURCE TEXT, so
 * an interpolated `min-w-[${n}px]` emits no rule — it lands in the DOM and does
 * nothing, which looks exactly like a table that chose not to have a floor.)
 *
 * ⚠️ **EACH FLOOR SITS BELOW ITS OWN WIDTH REQUEST** (380 < 416, 102 < 112,
 * 161 < 176, 117 < 128). The width is what the column asks for under auto
 * layout; the floor is what its content may not be crushed under. Move the two
 * together or the column silently stops honouring one of them.
 */
const COLUMN_CLASS: Record<string, string> = {
  product: 'w-[26rem] min-w-[380px]',
  cycles: 'w-28 min-w-[102px]',
  revenue: 'w-44 min-w-[161px]',
  [SHARE_COLUMN]: 'w-32 min-w-[117px]',
};

/**
 * The two freeze props for one column, or nothing at all.
 *
 * ⚠️ **AN UNFROZEN CELL MUST CARRY NEITHER PROP** — `Table` reads the pair
 * together, and a positioned cell with no reason to be positioned paints over
 * the table's collapsed borders.
 */
function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => 'left' | 'right' | undefined,
  offsets: Record<string, number>,
): { frozen?: 'left' | 'right'; frozenOffset?: number } {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

/**
 * A figure with its unit, in the shape the sibling tabs already use.
 *
 * 🔴 **COMPACT SINCE 2026-09-13 — owner's decision that every money figure on
 * the Management Dashboard reads `£20.00K`.** *SUPERSEDED: the full grouped
 * decimal, `formatDecimalString(amount)`.*
 *
 * ⛔ **THE `title` IS NOT DECORATION — IT IS THE ONLY PLACE THE EXACT FIGURE
 * SURVIVES.** `1.20M` has discarded everything below the hundred thousand, so
 * two rows 40,000 apart now print the same three characters; without the
 * attribute the precise number exists nowhere on the page and the column stops
 * being data. It carries the currency CODE as well as the digits, because the
 * compacted head carries a symbol and `$` is four currencies.
 *
 * ⚠️ **`compactMoneyParts` is the ONE formatter** — a second
 * `Intl.NumberFormat` anywhere in this folder is how one tab starts disagreeing
 * with the tab beside it. **Only the UNIT is gold**, and the three pieces
 * render back to back with no whitespace so the cell's text stays exactly
 * `£20.00K` for a screen reader and for a copy; both rules are `CompactFigure`'s
 * and are not to be re-derived here by slicing the last character.
 *
 * ⚠️ **`whitespace-nowrap` AND `tabular` BOTH STILL EARN THEIR PLACE.** The
 * shorter string does not make wrapping safe — the column's `min-w` floor is
 * cut for content, not for this — and tabular figures are what keep a compacted
 * column aligned now that the digit COUNT varies between rows (`£813` against
 * `£1.20M`) rather than only the leading separators.
 */
function Money({
  amount,
  currency,
  strong,
}: {
  amount: string;
  currency: string;
  strong?: boolean;
}): ReactElement {
  return (
    <span
      className="inline-flex items-baseline justify-end gap-1 whitespace-nowrap"
      title={`${formatDecimalString(amount)} ${currency}`}
    >
      {/* No code span — the symbol already carries the unit, and the panel
          header names it. See `Money` in `RevenueCompanyTable.tsx`. */}
      <span className={cx('text-fg tabular', strong ? 'font-semibold' : 'font-medium')}>
        <CompactFigure parts={compactMoneyParts(amount, currency)} />
      </span>
    </span>
  );
}

function ChevronIcon({ open }: { open: boolean }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx(
        'h-4 w-4 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none',
        open && 'rotate-180',
      )}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * The share cell: a bar and the percentage that is the real answer.
 *
 * ⚠️ The bar is `aria-hidden` and its width comes from a `Number()` — geometry,
 * never a figure. The percentage beside it is what a screen reader gets, and it
 * is an em dash rather than `0%` wherever no share could be computed.
 */
function ShareCell({ share }: { share: number | null }): ReactElement {
  const width = share === null || !Number.isFinite(share) ? 0 : Math.min(100, Math.max(0, share));

  return (
    <span className="flex items-center justify-end gap-2">
      <span
        aria-hidden="true"
        className="hidden h-1.5 w-14 overflow-hidden rounded-xs bg-surface-sunken sm:block"
      >
        <span className="block h-full rounded-xs bg-fg-secondary" style={{ width: `${width}%` }} />
      </span>
      <span className="w-12 text-xs text-fg-secondary tabular">{shareText(share)}</span>
    </span>
  );
}

/**
 * The product's name, and everything that qualifies it.
 *
 * 🔴 **THREE MARKS, EACH FOR A DIFFERENT WRONG READING**: the uncatalogued
 * badge (this is not a product), the label note (this label is not a name —
 * see `productLabelOf`), and the other-currency mark (the figure beside it is
 * partial and nothing converts between the two).
 */
function ProductCell({ group }: { group: ProductGroup }): ReactElement {
  const { label, focusRegions } = group;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-sm font-medium text-fg [overflow-wrap:anywhere]">{label.text}</span>
        {/*
         * The code beside a NAME, never instead of one. Where the code IS the
         * label, `label.note` says so and this would print it twice.
         */}
        {label.kind === 'named' && label.code !== null && (
          <span className="text-2xs text-fg-subtle">{label.code}</span>
        )}
      </span>

      {group.isUncoded && (
        <span className="flex">
          {/*
           * ⚠️ The `title` carries what the bucket IS — a value the short badge
           * cannot hold — and the badge's own wording already says it is not a
           * product, so nothing a reader needs lives only in the tooltip.
           */}
          <Badge
            tone="warning"
            title={label.kind === 'unidentified' ? NO_PRODUCT_MEANING : XERO_UNCODED_MEANING}
          >
            {UNCODED_BADGE}
          </Badge>
        </span>
      )}

      {/*
       * ⚠️ **WHICH OF THE THREE IDENTIFIERS THIS LABEL CAME FROM, when it is not
       * a name.** `id`, `name` and `code` are independently nullable and the
       * three absences mean different things; folding them into one string
       * would answer a different question per row with nothing saying which.
       */}
      {label.note !== null && !group.isUncoded && (
        <span className="text-2xs text-fg-subtle">{label.note}</span>
      )}

      {/*
       * Where the money RAN, in one line. The full breakdown with figures is one
       * click down; this is the shape of it.
       *
       * 🔴 **REGIONS OF FOCUS, NOT BILLING REGIONS — owner's instruction
       * 2026-09-13.** ⚠️ *SUPERSEDED: `bands.length` and `"N billing regions"`.*
       * The billing region is still the ROW's band and still has its control in
       * the bar above; what moved is the breakdown. **The two must not be worded
       * alike**, because they answer different questions and disagree routinely:
       * a project runs where the work happens, a billing region is where the
       * customer pays from.
       *
       * ⚠️ **A SUB-LINE RATHER THAN A COLUMN OF ITS OWN, and there is no funnel
       * here.** A funnel on a region column could only ever drop whole ROWS,
       * leaving each surviving product carrying every region's money under a
       * heading that says otherwise. The bar's control narrows the ROWS BEFORE
       * they are grouped, so its figures really are that band's.
       *
       * ⚠️ **A COUNT, NEVER A SHARE.** These figures sum higher than the row —
       * see `ProductGroup.focusRegions` — so anything proportional here would be
       * a percentage that can exceed 100.
       */}
      {focusRegions.length > 0 && (
        <ValuePills
          items={focusRegions.map((region) => ({
            key: region.key,
            label: region.label,
            title: region.meaning,
          }))}
        />
      )}

      {/*
       * 🔴 **THE ONLY MARK SAYING THE FIGURE BESIDE IT IS PARTIAL.** This product
       * was also sold in another currency and appears on that currency's list
       * with a different number. Neither is "what this product earns", and
       * nothing converts between them.
       *
       * ⚠️ **ONE BADGE LISTING EVERY OTHER CURRENCY, not one badge each.** Two
       * pills reading "Also sold in EUR" and "Also sold in GBP" stood on their
       * own line on most of the 27 rows and were the bulk of the table's height.
       * The codes are joined instead — the claim is identical and it is one
       * line. **The badge itself is not optional**: without it the figure beside
       * it reads as what the product earns.
       */}
      {group.alsoSoldIn.length > 0 && (
        <span className="flex min-w-0 flex-wrap items-center gap-1">
          <Badge tone="neutral" variant="outline">Also sold in</Badge>
          <ValuePills items={namePills(group.alsoSoldIn)} />
        </span>
      )}
    </div>
  );
}

/**
 * One product's cycle-by-cycle and region-by-region detail, opened in place.
 *
 * 🔴 **THE REGION HALF IS THE REGION OF **FOCUS** SINCE 2026-09-13, on the
 * owner's instruction.** ⚠️ *SUPERSEDED: "By billing region", one figure per band,
 * summing exactly to the row total.* **The billing region has not been retired** —
 * it is still the row's band and still has its own control in the bar above; only
 * this breakdown moved.
 *
 * ⛔ **THE TWO COLUMNS OF THIS PANEL NOW OBEY DIFFERENT ARITHMETIC, WHICH IS THE
 * ONE THING TO KNOW BEFORE TOUCHING IT.** `By cycle` decomposes the row total —
 * its figures add up to it. `By region of focus` does **not**: a project naming
 * several regions is counted in full under each, so that column adds to more.
 * Side by side, the second looks like the first. That is what `overCount`'s
 * sentence exists to say, and why no share, bar or percentage may ever be drawn
 * against a focus figure.
 *
 * ⚠️ **EVERY CYCLE IS LISTED, INCLUDING THE EMPTY ONES.** This differs from the
 * company tab, which drops them — there the question is *"which years did this
 * customer trade"*, and here it is *"is this product still selling"*, for which
 * the gap between two selling years is the finding. A run of em dashes is the
 * answer rather than noise.
 *
 * ⚠️ **THIS IS NOW THE ONLY PLACE THE PER-CYCLE FIGURES APPEAR**, the sparkline
 * column having been removed on the owner's instruction. The figures were always
 * here; what went was a picture of them that could not state a value.
 */
function ProductBreakdown({
  group,
  currency,
}: {
  group: ProductGroup;
  currency: string;
}): ReactElement {
  return (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="min-w-0">
            <p className="m-0 border-b border-border pb-1 text-xs font-semibold text-fg">
              By cycle
            </p>
            <ul className="m-0 flex list-none flex-col p-0">
              {group.byCycle.map((entry) => (
                <li
                  key={entry.cycle.id}
                  className="flex items-baseline justify-between gap-3 py-1"
                >
                  <span className="text-xs text-fg-secondary">{entry.cycle.name}</span>
                  {entry.amount === null ? (
                    <span
                      className="text-xs text-fg-subtle"
                      title={`Nothing in ${currency} in ${entry.cycle.name}. This is an absence, not a total of zero.`}
                    >
                      —
                    </span>
                  ) : (
                    <Money amount={entry.amount} currency={currency} />
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="min-w-0">
            <p className="m-0 border-b border-border pb-1 text-xs font-semibold text-fg">
              By region of focus
            </p>

            {/*
             * 🔴 **THE ONE THING ON SCREEN THAT CAN SAY THIS LIST ADDS TO MORE
             * THAN THE ROW IT IS INSIDE.** A project naming several regions is
             * counted in full under each, on the owner's instruction — so without
             * this sentence the column reads as a decomposition of the row total
             * and every figure in it is quietly over-stated, with nothing looking
             * wrong. It renders ABOVE the list rather than under it, so the
             * qualifier reaches the reader before the figures do.
             *
             * ⚠️ **NOT HELPER TEXT — the documented fact-about-the-data
             * exception**, and it must survive a sweep. `overCountSentence` is the
             * one place it is worded, and it deliberately states all three figures
             * rather than an inequality or a difference: the principal on the wire
             * is the money counted ONCE, never the excess.
             *
             * ⚠️ **`overCount === null` IS "NOTHING OVER-COUNTED"**, so no line —
             * never a sentence reading `0.00`.
             */}
            {group.overCount !== null && (
              /*
               * ⚠️ **`text-xs`, NOT THE `text-2xs` THE SUB-LINES USE.** This is
               * three figures and a reconciliation between them, not a marginal
               * note — `text-fg-muted` is what keeps it out of the way of the
               * list, and shrinking it further is how the one sentence qualifying
               * every figure below becomes the one nobody reads.
               */
              <p className="m-0 pt-1.5 text-xs text-fg-muted">
                {overCountSentence(group.overCount, currency)}
              </p>
            )}

            {!group.focusReported ? (
              /*
               * 🔴 **"THE SERVER DID NOT SAY" IS NOT "IN NO REGION".** An older
               * API omits the breakdown entirely, and drawing the empty list that
               * an in-no-region product draws would turn a gap in the response
               * into a claim about this product. Named rather than blank, for the
               * same reason the no-billing-region band is.
               */
              <p className="m-0 py-1 text-xs text-fg-subtle">
                No region-of-focus breakdown was reported for this product.
              </p>
            ) : group.focusRegions.length === 0 ? (
              <p
                className="m-0 py-1 text-xs text-fg-subtle"
                title={`No region of focus carries a figure in ${currency} for this scope. This is an absence, not a total of zero.`}
              >
                No region of focus carries a figure here.
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col p-0">
                {group.focusRegions.map((entry) => (
                  <li key={entry.key} className="flex items-baseline justify-between gap-3 py-1">
                    {/*
                     * ⚠️ **THE THREE KINDS ARE WORDED APART AND THE `title`
                     * CARRIES WHICH.** A named region, a project nobody has
                     * classified, and work never sold through a project are three
                     * different facts — and only the middle one is somebody's job,
                     * so the two absences must not read alike. `FOCUS_MEANING`
                     * says which; the label alone cannot.
                     *
                     * ⚠️ **THE TWO ABSENCES ARE MUTED, so a reader scanning the
                     * list can see at a glance which entries are regions** —
                     * a second signal beside the wording, never the only one.
                     */}
                    <span
                      className={cx(
                        'min-w-0 text-xs [overflow-wrap:anywhere]',
                        entry.kind === 'region' ? 'text-fg-secondary' : 'text-fg-subtle italic',
                      )}
                      title={entry.meaning}
                    >
                      {entry.label}
                    </span>
                    <Money amount={entry.amount} currency={currency} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
  );
}

/** `true` when two decimal strings are the same amount, whatever their scale. */
function sameAmount(a: string | null, b: string | null): boolean {
  if (a === null || b === null) return a === b;
  const negated = b.trim().startsWith('-') ? b.trim().slice(1) : `-${b.trim()}`;
  const difference = sumDecimalStrings([a, negated]);
  return difference !== null && /^-?0(\.0+)?$/.test(difference);
}

const EXPAND_VIEW_OPTIONS = [
  { value: 'table', label: 'Table' },
  { value: 'breakdown', label: 'Breakdown' },
] as const;

/** A company row's name, as a link to its record. */
function companyRowLabel(row: Pick<MatrixRow, 'key' | 'label'>): ReactNode {
  const id = companyIdOfMatrixKey(row.key);
  if (id === null) return row.label;
  return <TextLink to={`/companies/${id}`}>{row.label}</TextLink>;
}

/**
 * One product's companies × cycles matrix.
 *
 * 🔴 **BUILT FROM THE SAME CELLS, THROUGH THE SAME UNIT GATE, AS THE PRODUCT
 * ROW'S TOTAL** — see `companyLinesOf`. The dev-only check below says so loudly
 * the day the server's company split stops summing to its own row.
 */
function ProductCompanyMatrix({
  group,
  lines,
  scopeCycles,
  todaysCycleId,
  currency,
  taxLabel,
  visibility,
}: {
  group: ProductGroup;
  lines: CompanyMatrixLine[];
  scopeCycles: readonly RevenueProductCycle[];
  todaysCycleId: number | null;
  currency: string;
  taxLabel: string;
  visibility: MatrixVisibilityProps;
}): ReactElement {
  const matrix = useMemo(
    () =>
      buildProductCompanyMatrix(lines, matrixCyclesOf(scopeCycles), {
        currentCycleId: todaysCycleId === null ? null : String(todaysCycleId),
      }),
    [lines, scopeCycles, todaysCycleId],
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!sameAmount(matrix.grandTotal, group.total)) {
      console.error('[RevenueProductTable] matrix grand total differs from the product row total', {
        product: group.label.text,
        matrixGrandTotal: matrix.grandTotal,
        rowTotal: group.total,
      });
    }
  }, [matrix, group.total, group.label.text]);

  return (
    <RevenueMatrixTable
      matrix={matrix}
      currencyCode={currency}
      title="Revenue by company"
      subjectName={group.label.text}
      rowHeader="Company"
      basisLabel={`Paid, ${taxLabel}`}
      csvFileName={`${group.label.text}-revenue-by-company.csv`}
      renderRowLabel={companyRowLabel}
      visibility={visibility}
    />
  );
}

/**
 * One product's detail, opened in place: the companies × cycles matrix by
 * default, or the cycle and region-of-focus breakdown.
 *
 * ⚠️ **THE MATRIX IS WITHHELD AGAINST A SERVER THAT DOES NOT REPORT COMPANIES**
 * (`group.companyLines === null`). The breakdown is then shown with no toggle.
 *
 * ⚠️ **`w-0 min-w-full` ON THE WRAPPER KEEPS A WIDE MATRIX SCROLLING INSIDE THE
 * CELL** rather than widening the whole ranked table — the same arrangement the
 * company tab uses.
 */
function ProductDetail({
  group,
  currency,
  columnCount,
  scopeCycles,
  todaysCycleId,
  coversAllCycles,
  view,
  onViewChange,
  taxLabel,
  visibility,
}: {
  /**
   * The table's hidden years and companies — one instance for every expanded
   * product. ⚠️ The Breakdown view is not narrowed by it: its by-cycle and
   * by-region figures are the product's own server totals, with no per-company
   * split to remove a hidden company from.
   */
  visibility: MatrixVisibilityProps;
  coversAllCycles: boolean;
  group: ProductGroup;
  currency: string;
  taxLabel: string;
  /**
   * 🔴 **THE LIVE VISIBLE COLUMN COUNT, NEVER A LITERAL.**
   *
   * ⚠️ *SUPERSEDED: a module-level `COLUMN_COUNT = 4`.* That was right while the
   * table had exactly one shape; with columns hideable it has as many shapes as
   * its columns have subsets, and a stale `colSpan` **fails silently** — one too
   * large makes this row overhang the table, one too small draws a rule that
   * stops short of the edge. Nothing errors and `tsc` stays green, so the only
   * symptom is a line in the wrong place. It is passed rather than recomputed so
   * the header, the body and this row all read the one list.
   */
  columnCount: number;
  scopeCycles: readonly RevenueProductCycle[];
  todaysCycleId: number | null;
  view: ProductExpandView;
  onViewChange: (view: ProductExpandView) => void;
}): ReactElement {
  const lines = group.companyLines;
  const shown: ProductExpandView = lines === null ? 'breakdown' : view;

  return (
    <TR className="bg-surface-sunken">
      <TD colSpan={columnCount} verticalAlign="top" className="px-4 py-3">
        <div className="flex w-0 min-w-full flex-col gap-3">
          {/*
           * A fact about the data (the documented exception to the no-helper-text
           * rule): what this product took in its ORIGINAL currencies, one figure
           * per currency, never summed across them. Hidden on an API that does
           * not report it. It covers every cycle, so under a one-cycle scope the
           * line says so rather than reading as that cycle's money.
           */}
          {group.nativeTotals !== null && group.nativeTotals.length > 0 && (
            <p className="m-0 text-xs text-fg-muted">
              {coversAllCycles ? 'In original currencies: ' : 'In original currencies, all cycles: '}
              <span className="tabular text-fg-secondary">
                {nativeTotalsText(group.nativeTotals)}
              </span>
            </p>
          )}

          {lines !== null && (
            <div className="flex justify-end">
              <ViewToggle
                label={`How to show who bought ${group.label.text}`}
                value={view}
                options={EXPAND_VIEW_OPTIONS}
                onChange={onViewChange}
              />
            </div>
          )}

          {shown === 'table' && lines !== null ? (
            /*
             * ⚠️ `isolate`: the nested matrix's header cells inherit the outer
             * table's sticky `z-[15]`, and without their own stacking context
             * they would paint over the outer pinned header row as it scrolls.
             */
            <div className="isolate min-w-0 overflow-x-auto">
              <ProductCompanyMatrix
                group={group}
                lines={lines}
                scopeCycles={scopeCycles}
                todaysCycleId={todaysCycleId}
                currency={currency}
                taxLabel={taxLabel}
                visibility={visibility}
              />
            </div>
          ) : (
            <ProductBreakdown group={group} currency={currency} />
          )}
        </div>
      </TD>
    </TR>
  );
}

/* -------------------------------------------------------------------------
 * The table
 * --------------------------------------------------------------------- */

/**
 * WHY THERE IS NOTHING TO SHOW — decided by the PANEL, because only the panel
 * can tell the four apart.
 *
 * 🔴 **THE SERVER SEARCH AND THE BROWSER FILTERS FAIL DIFFERENTLY AND MUST NOT
 * SHARE A SENTENCE.** A search that matched no company returns zero ROWS; a
 * browser filter that matched nothing returns rows and keeps none of them. From
 * inside this component the two are identical — an empty `groups` — so the
 * distinction is passed in rather than guessed at.
 *
 * ⚠️ **`no-revenue` IS A STATEMENT ABOUT THE BUSINESS AND THE OTHERS ARE
 * STATEMENTS ABOUT A CONTROL.** Reading one as the other is how a currency
 * nobody trades in gets reported as a catalogue nobody is buying.
 */
export type ProductTableEmptyReason =
  /** The server matched no company. Carries the term, so it can be quoted back. */
  | { kind: 'no-search-match'; term: string }
  /** Rows came back and the browser filters kept none of them. */
  | { kind: 'filtered' }
  /** Nothing was narrowed. There is simply no revenue in this currency and scope. */
  | { kind: 'no-revenue' };

export function RevenueProductTable({
  groups,
  total,
  currency,
  taxLabel,
  scopeLabel,
  orderNote,
  hiddenByColumnFilters,
  headerFor,
  loading,
  emptyReason,
  onClearFilters,
  scopeCycles,
  todaysCycleId,
  coversAllCycles,
}: {
  /** Whether the cycle scope is "All cycles" (words the native-totals line). */
  coversAllCycles: boolean;
  /** The products on screen, already filtered and ordered. */
  groups: readonly ProductGroup[];
  /** The cycles the figures cover, in date order — the matrix's columns. */
  scopeCycles: readonly RevenueProductCycle[];
  /** The id of the cycle containing today, or `null` when today is in none. */
  todaysCycleId: number | null;
  /** The exact sum of `groups`, which every share is taken against. */
  total: string | null;
  currency: string;
  /** `incl. tax` or `ex-tax`, from the server's `meta.measure` echo. */
  taxLabel: string;
  /** What the reader chose, for the heading — "All cycles" or a cycle's name. */
  scopeLabel: string;
  /**
   * How the rows are ordered, **only while no column header says so**.
   *
   * ⚠️ **THE OPENING ORDER IS REVENUE-DESCENDING AND NO ARROW REPORTS IT.**
   * `useTableFilters` starts with `sort: null`, so every header renders
   * unsorted while the list is in fact ranked. Without this the heading would
   * describe a table whose order nothing on screen accounts for.
   */
  orderNote: string | null;
  /**
   * Products the column funnels are holding back.
   *
   * ⚠️ **STATED RATHER THAN LEFT TO THE FILLED FUNNEL.** A shorter table is
   * indistinguishable from a catalogue nobody is buying, and the funnel icon
   * sits in a header a reader may have scrolled past horizontally.
   */
  hiddenByColumnFilters: number;
  /** From `useTableFilters` in the panel. See the note on the header row. */
  headerFor: (key: string) => FilterableTHProps;
  loading: boolean;
  /** Why an empty list is empty. See {@link ProductTableEmptyReason}. */
  emptyReason: ProductTableEmptyReason;
  onClearFilters: () => void;
}): ReactElement {
  const [expandedKeys, setExpandedKeys] = useRememberedState<readonly string[]>(
    'revenue-product-expanded',
    [],
    parseStringArray,
  );
  const expanded = useMemo<ReadonlySet<string>>(() => new Set(expandedKeys), [expandedKeys]);
  const [showAll, setShowAll] = useRememberedState('revenue-product-show-all', false);

  /*
   * How an expanded product is shown — one choice for the whole table,
   * remembered on the account (filter memory), seeded from this browser's older
   * local choice. Table (the matrix) unless the reader picked Breakdown.
   */
  const [expandView, setExpandView] = useRememberedState<ProductExpandView>(
    'revenue-product-expand-view',
    readProductExpandView,
    (raw) => (raw === 'table' || raw === 'breakdown' ? raw : undefined),
  );
  const changeExpandView = (view: ProductExpandView) => {
    setExpandView(view);
    writeProductExpandView(view);
  };

  /*
   * The reader's own arrangement of this table — order, hidden set, frozen
   * edges — read from and written back to their account.
   */
  const layout = useColumnLayout(LAYOUT_SCOPE, COLUMN_KEYS, LOCKED_KEYS);

  /*
   * What the reader has hidden INSIDE an expanded product — years and companies
   * (owner, 2026-10-27). Mounted HERE, once, so every expanded product reads the
   * same choice; see `matrix-visibility.ts`. ⚠️ This ranked table has no
   * per-year columns of its own — the panel's cycle filter stays the control.
   */
  const matrixCycles = useMatrixVisibility(MATRIX_VISIBILITY_SCOPES.productMatrixCycles);
  const matrixCompanies = useMatrixVisibility(MATRIX_VISIBILITY_SCOPES.productMatrixCompanies);
  const matrixVisibility = useMemo<MatrixVisibilityProps>(
    () => ({ cycles: matrixCycles, rows: matrixCompanies, rowsTitle: 'Companies' }),
    [matrixCycles, matrixCompanies],
  );

  /*
   * ⚠️ **THE OFFSETS ARE MEASURED FROM THE HEADER ROW, because there is no
   * `table-fixed` and no `<colgroup>` here** — the `w-*` on each header cell is
   * a request the content can outvote, so the only honest source for how far a
   * frozen column sits from its edge is the DOM.
   */
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, layout.visibleKeys, layout.frozenSideOf);

  /**
   * The manager's label per key.
   *
   * ⚠️ **THREE OF THE FOUR ARE READ BACK FROM THE PANEL'S OWN COLUMN SPECS**, so
   * the manager and the header cannot call one column two things — the panel
   * owns those labels and has already changed one of them once.
   *
   * ⛔ **`share` MUST BE ANSWERED HERE AND NOT ASKED OF `headerFor`.** That
   * function returns `{ label: key }` for a key no column declares, so asking it
   * would put a lower-case `share` in the list — a wrong answer rather than a
   * missing one, which is the shape this file's header already warns about.
   */
  const labelOf = (key: string): string =>
    key === SHARE_COLUMN ? 'Share' : headerFor(key).label;

  const toggle = (key: string): void => {
    setExpandedKeys((current) =>
      current.includes(key) ? current.filter((open) => open !== key) : [...current, key],
    );
  };

  const visible = showAll ? groups : groups.slice(0, HEAD_ROWS);
  const hidden = groups.length - visible.length;

  /*
   * ⚠️ **THE SPREAD COMES FIRST AND `className` AFTER IT.** `headerFor` never
   * returns a `className`, so the order cannot clobber anything today — but the
   * app's own idiom is spread-then-width, and the reverse form elsewhere works
   * only by that accident.
   *
   * 🔴 **A KEY NO COLUMN DECLARES RENDERS A PLAIN HEADER WITH NO FUNNEL AT ALL**
   * — `useTableFilters.headerFor` returns `{ label: key }` for an unknown key
   * rather than throwing. That is the safe direction: a missing control is
   * something somebody reports, where a sort arrow that silently refuses every
   * click is something they work around. The sort keys themselves are checked at
   * COMPILE time; see `PRODUCT_SORT_KEYS`.
   */
  const headCell = (key: string): ReactElement | null => {
    const frozen = frozenPropsFor(key, layout.frozenSideOf, frozenOffsets);
    const className = COLUMN_CLASS[key];

    switch (key) {
      case 'product':
        return <FilterableTH key={key} {...headerFor('product')} className={className} {...frozen} />;
      case 'cycles':
        return (
          <FilterableTH key={key} {...headerFor('cycles')} numeric className={className} {...frozen} />
        );
      case 'revenue':
        return (
          <FilterableTH
            key={key}
            {...headerFor('revenue')}
            numeric
            className={className}
            {...frozen}
          />
        );
      case SHARE_COLUMN:
        /* No funnel — see the header note on this file. */
        return (
          <TH key={key} numeric className={className} {...frozen}>
            Share
          </TH>
        );
      default:
        return null;
    }
  };

  /*
   * ⚠️ **THE CELL FOR EACH KEY IS A SWITCH, NOT A LOOKUP OBJECT.** Every arm
   * keeps the comment that was attached to the cell it replaces — those
   * paragraphs record why each control and each mark is what it is, and a table
   * of JSX would have stranded them from the markup they describe.
   *
   * ⚠️ **THE HEADER AND THE BODY WALK THE SAME LIST**, which is what stops a
   * heading ending up over the wrong column — a failure no type and no build
   * can see.
   */
  const bodyCell = (key: string, group: ProductGroup, isOpen: boolean): ReactElement | null => {
    const frozen = frozenPropsFor(key, layout.frozenSideOf, frozenOffsets);

    switch (key) {
      case 'product':
        return (
          <TD key={key} verticalAlign="top" {...frozen}>
            <div className="flex min-w-0 items-start gap-2">
              {/*
               * ⚠️ **A BUTTON, and the only control on the row.** Unlike the
               * company table there is no link out: a product has no detail
               * page in this app, so making the row a link would promise a
               * destination that does not exist.
               *
               * 🔴 **AND IT IS WHY THIS COLUMN IS LOCKED.** Hiding it would
               * take away the only route into the breakdown. See `LOCKED_KEYS`.
               */}
              <Tooltip
                label={`${isOpen ? 'Hide' : 'Show'} breakdown`}
                describes={false}
              >
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Hide' : 'Show'} the cycle and region-of-focus breakdown for ${group.label.text}`}
                  onClick={() => toggle(group.key)}
                  className="mt-0.5 shrink-0 rounded-sm p-0.5 text-fg-subtle transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none"
                >
                  <ChevronIcon open={isOpen} />
                </button>
              </Tooltip>
              <ProductCell group={group} />
            </div>
          </TD>
        );

      case 'cycles':
        return (
          <TD
            key={key}
            numeric
            verticalAlign="top"
            className="text-xs text-fg-secondary tabular"
            {...frozen}
          >
            {group.activeCycles}
          </TD>
        );

      case 'revenue':
        return (
          <TD key={key} numeric verticalAlign="top" {...frozen}>
            <Money amount={group.total} currency={currency} strong />
          </TD>
        );

      case SHARE_COLUMN:
        return (
          <TD key={key} numeric verticalAlign="top" {...frozen}>
            <ShareCell share={shareOf(group.total, total)} />
          </TD>
        );

      default:
        return null;
    }
  };

  const header = (
    <THead>
      {/*
       * The row `useFrozenOffsets` measures. See the note at the hook call —
       * the rendered widths are only knowable from the DOM.
       */}
      <TR ref={headRowRef}>{layout.visibleKeys.map((key) => headCell(key))}</TR>
    </THead>
  );

  const rowFor = (group: ProductGroup): ReactElement => {
    const isOpen = expanded.has(group.key);

    return (
      <Fragment key={group.key}>
        <TR>{layout.visibleKeys.map((key) => bodyCell(key, group, isOpen))}</TR>

        {isOpen && (
          <ProductDetail
            group={group}
            currency={currency}
            columnCount={layout.visibleKeys.length}
            scopeCycles={scopeCycles}
            todaysCycleId={todaysCycleId}
            coversAllCycles={coversAllCycles}
            view={expandView}
            onViewChange={changeExpandView}
            taxLabel={taxLabel}
            visibility={matrixVisibility}
          />
        )}
      </Fragment>
    );
  };

  return (
    <Panel className="min-w-0">
      <PanelHeader
        title="Revenue by product"
        /*
         * The heading carries the three qualifiers every figure below depends
         * on. A table headed "Revenue" is a table somebody quotes as revenue.
         */
        description={`Paid, ${taxLabel} · ${currency} · ${scopeLabel} · ${pluralise(groups.length, 'product')}${
          orderNote === null ? '' : ` · ${orderNote}`
        }`}
        /*
         * ⚠️ **THE TRIGGER LIVES IN THIS PANEL'S OWN HEADER, which is rendered
         * here rather than by the tab above** — so the control reaches the table
         * it configures without a parent having to pass anything down.
         *
         * ⚠️ **WITHHELD UNTIL THE STORED LAYOUT HAS ARRIVED, and that is not
         * cosmetic.** Until the read resolves the controller holds the DEFAULT
         * layout, so a toggle pressed in that window would persist a hidden set
         * derived from nothing and overwrite the arrangement still in flight — a
         * `200`, no error, and a column the reader had hidden quietly back.
         *
         * ⚠️ **IT IS NOT GATED ON THE ROWS.** It renders over the empty state
         * too, because unhiding a column is the only way back for somebody who
         * hid one and then filtered the list to nothing.
         */
        {...(layout.loading
          ? {}
          : {
              actions: (
                <ColumnManager
                  layout={layout}
                  labelOf={labelOf}
                  lockedKeys={LOCKED_KEYS}
                  lockReasonOf={lockReasonOf}
                />
              ),
            })}
      />

      {loading ? (
        <div className="flex flex-col gap-2 px-4 py-4">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <Skeleton key={n} className="h-6 w-full" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState
          title={
            emptyReason.kind === 'no-search-match'
              ? `No company matches “${emptyReason.term}”`
              : emptyReason.kind === 'filtered'
                ? 'No products match these filters'
                : `No ${currency} revenue to report`
          }
          description={
            emptyReason.kind === 'no-search-match'
              ? /*
                 * ⚠️ **NAMES WHERE THE SEARCH RAN.** It is the one control that
                 * goes to the server, across every currency and cycle — so a
                 * reader who reaches for the currency next is reaching for the
                 * wrong control, and nothing else on screen would tell them.
                 */
                `The company search is applied by the server, across every currency and cycle. No company matching “${emptyReason.term}” has a paid invoice.`
              : emptyReason.kind === 'filtered'
                ? 'Clear the filters to see every product again. This clears the column filters as well as the controls above.'
                : /*
                   * ⚠️ **NAMES THE CURRENCY, because it is the one control whose
                   * effect a reader cannot see**: an empty page under GBP looks
                   * exactly like a catalogue nobody is buying.
                   */
                  `No paid invoice line in ${currency} falls in this scope.`
          }
          {...(emptyReason.kind === 'no-revenue'
            ? {}
            : {
                action: (
                  <Button variant="secondary" onClick={onClearFilters}>
                    Clear filters
                  </Button>
                ),
              })}
        />
      ) : (
        /*
         * The tab's main table: the header row stays pinned while it scrolls
         * in its own viewport-tall area (owner 2026-10-18).
         */
        <TableWrap frozenHeader>
          {/*
           * ⚠️ **THE TABLE'S OWN `min-w-[760px]` IS GONE, AND THE FLOOR IS NOT.**
           * It lives on the cells now, as `COLUMN_CLASS`'s four `min-w-` shares —
           * which sum to the same 760 while every column is showing, and drop by
           * a column's own share when it is hidden. A fixed 760 over three
           * columns would have held width for one that is not there, which reads
           * as a table that will not narrow.
           */}
          <Table>
            {header}
            <TBody>{visible.map((group) => rowFor(group))}</TBody>
          </Table>
        </TableWrap>
      )}

      {/*
       * ⚠️ **WHAT IS BEING HELD BACK IS STATED, NOT IMPLIED**, whether it is the
       * head limit or a column funnel. A short table that does not say how much
       * it is holding back is indistinguishable from a filter that matched
       * little — and on this tab the difference is three fifths of the money.
       */}
      {!loading && groups.length > 0 && (hiddenByColumnFilters > 0 || groups.length > HEAD_ROWS) && (
        <PanelFooter>
          <span className="text-xs text-fg-muted">
            {hiddenByColumnFilters > 0
              ? `${pluralise(hiddenByColumnFilters, 'product')} hidden by the column filters.`
              : hidden > 0
                ? `Showing the first ${visible.length.toLocaleString()} of ${groups.length.toLocaleString()} products.`
                : `Showing all ${groups.length.toLocaleString()} products.`}
          </span>

          {groups.length > HEAD_ROWS && (
            /*
             * ⚠️ **IT TOGGLES BOTH WAYS.** A one-way "Show all" leaves a reader
             * who pressed it scrolling the whole catalogue for the rest of the
             * session with no control on screen to undo what they just did.
             */
            <Button variant="secondary" onClick={() => setShowAll((open) => !open)}>
              {hidden > 0 ? `Show all ${groups.length.toLocaleString()}` : `Show first ${HEAD_ROWS}`}
            </Button>
          )}
        </PanelFooter>
      )}
    </Panel>
  );
}
